import { useEffect, useMemo } from "react";
import { Link } from "react-router";

import privacyPolicyMarkdown from "../../../../../docs/legal/privacy-policy.md?raw";
import termsOfServiceMarkdown from "../../../../../docs/legal/terms-of-service.md?raw";
import cookiePolicyMarkdown from "../../../../../docs/legal/cookie-policy.md?raw";
import dataProcessingAgreementMarkdown from "../../../../../docs/legal/data-processing-agreement.md?raw";

type LegalDocumentKey = "privacy" | "terms" | "cookies" | "dpa";

interface LegalDocumentDefinition {
  key: LegalDocumentKey;
  title: string;
  description: string;
  lastUpdated: string;
  canonicalPath: string;
  markdown: string;
}

const LEGAL_DOCUMENTS: Record<LegalDocumentKey, LegalDocumentDefinition> = {
  privacy: {
    key: "privacy",
    title: "Privacy Policy",
    description: "How Stero processes personal data, handles sub-processors, and supports your privacy rights.",
    lastUpdated: "March 27, 2026",
    canonicalPath: "/privacy",
    markdown: privacyPolicyMarkdown,
  },
  terms: {
    key: "terms",
    title: "Terms of Service",
    description: "The terms governing use of the Stero AI platform and related services.",
    lastUpdated: "March 27, 2026",
    canonicalPath: "/terms",
    markdown: termsOfServiceMarkdown,
  },
  cookies: {
    key: "cookies",
    title: "Cookie Policy",
    description: "How Stero uses essential, analytics, and marketing cookies across the platform.",
    lastUpdated: "March 27, 2026",
    canonicalPath: "/cookies",
    markdown: cookiePolicyMarkdown,
  },
  dpa: {
    key: "dpa",
    title: "Data Processing Agreement",
    description: "The Data Processing Agreement that supplements the Stero subscription relationship for controller-processor obligations.",
    lastUpdated: "March 27, 2026",
    canonicalPath: "/legal/dpa",
    markdown: dataProcessingAgreementMarkdown,
  },
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;");
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function sanitizeUrl(url: string): string {
  const decoded = url.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");
  return /^(https?:\/\/|mailto:|\/[^/])/i.test(decoded) ? url : "#";
}

function markdownToHtml(markdown: string): string {
  let html = markdown
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  html = html.replace(/^---$/gm, "<hr>");

  html = html.replace(
    /^(\|.+\|)\n(\|[-| :]+\|)\n((?:\|.+\|\n?)*)/gm,
    (_match, headerRow: string, _separator: string, bodyRows: string) => {
      const headers = headerRow.split("|").filter(Boolean).map((item: string) => item.trim());
      const headerHtml = headers.map((item: string) => `<th>${item}</th>`).join("");

      const rows = bodyRows.trim().split("\n").filter(Boolean);
      const bodyHtml = rows.map((row: string) => {
        const cells = row.split("|").filter(Boolean).map((item: string) => item.trim());
        return `<tr>${cells.map((item: string) => `<td>${item}</td>`).join("")}</tr>`;
      }).join("");

      return `<div class="table-wrap"><table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`;
    },
  );

  html = html.replace(/^#### (.+)$/gm, (_match, text: string) => `<h4 id="${slugify(text)}">${text}</h4>`);
  html = html.replace(/^### (.+)$/gm, (_match, text: string) => `<h3 id="${slugify(text)}">${text}</h3>`);
  html = html.replace(/^## (.+)$/gm, (_match, text: string) => `<h2 id="${slugify(text)}">${text}</h2>`);
  html = html.replace(/^# (.+)$/gm, (_match, text: string) => `<h1>${text}</h1>`);
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, text: string, href: string) => `<a href="${sanitizeUrl(href)}" rel="noopener noreferrer">${text}</a>`,
  );

  html = html.replace(
    /^((?:- .+\n?)+)/gm,
    (block: string) => {
      const items = block.trim().split("\n").map((line: string) => `<li>${line.replace(/^- /, "")}</li>`).join("");
      return `<ul>${items}</ul>`;
    },
  );

  html = html.replace(
    /^((?:\d+\. .+\n?)+)/gm,
    (block: string) => {
      const items = block.trim().split("\n").map((line: string) => `<li>${line.replace(/^\d+\. /, "")}</li>`).join("");
      return `<ol>${items}</ol>`;
    },
  );

  return html
    .split("\n\n")
    .map((block: string) => {
      const trimmed = block.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("<")) return trimmed;
      return `<p>${trimmed.replace(/\n/g, "<br>")}</p>`;
    })
    .join("\n");
}

function extractToc(markdown: string): Array<{ level: number; text: string; id: string }> {
  const toc: Array<{ level: number; text: string; id: string }> = [];
  for (const line of markdown.split("\n")) {
    const match = line.match(/^(#{2,3}) (.+)$/);
    if (!match) continue;
    const headingHashes = match[1];
    const headingText = match[2];
    if (!headingHashes || !headingText) continue;
    toc.push({
      level: headingHashes.length,
      text: headingText,
      id: slugify(headingText),
    });
  }
  return toc;
}

export function LegalDocumentPage({ documentKey }: { documentKey: LegalDocumentKey }) {
  const documentDefinition = LEGAL_DOCUMENTS[documentKey];
  const toc = useMemo(() => extractToc(documentDefinition.markdown), [documentDefinition.markdown]);
  const html = useMemo(() => markdownToHtml(documentDefinition.markdown), [documentDefinition.markdown]);

  useEffect(() => {
    document.title = `${documentDefinition.title} | Stero AI`;
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute("content", documentDefinition.description);
    }
  }, [documentDefinition.description, documentDefinition.title]);

  return (
    <div className="min-h-screen bg-[#fcfcfd] text-[#1a1a2e]">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
        <header className="border-b border-black/8 pb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-base font-semibold tracking-tight text-[#1040CC]">
            Stero AI
          </Link>
          <div className="mt-6 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#1040CC]/70">
              Legal
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-[#111827]">
              {documentDefinition.title}
            </h1>
            <p className="mt-4 text-base leading-7 text-[#4b5563]">
              {documentDefinition.description}
            </p>
            <p className="mt-4 text-sm text-[#6b7280]">
              Last Updated: {documentDefinition.lastUpdated}
            </p>
          </div>
        </header>

        <div className="mt-8 grid gap-8 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <aside className="self-start rounded-2xl border border-black/8 bg-white/90 p-5 shadow-[0_18px_50px_-35px_rgba(16,64,204,0.35)]">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6b7280]">
              Contents
            </h2>
            <ol className="mt-4 space-y-2">
              {toc.map((item) => (
                <li key={item.id} className={item.level === 3 ? "pl-4" : ""}>
                  <a href={`#${item.id}`} className="text-sm leading-6 text-[#4b5563] transition hover:text-[#1040CC]">
                    {item.text}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <article className="rounded-[28px] border border-black/8 bg-white px-6 py-8 shadow-[0_24px_60px_-40px_rgba(17,24,39,0.28)] sm:px-8">
            <div
              className="legal-markdown"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </article>
        </div>

        <footer className="mt-10 border-t border-black/8 pt-6 text-sm text-[#6b7280]">
          <p>
            Contact: <a href="mailto:privacy@stero.ai" className="text-[#1040CC] hover:underline">privacy@stero.ai</a>
          </p>
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
            {Object.values(LEGAL_DOCUMENTS).map((item) => (
              <Link key={item.key} to={item.canonicalPath} className="text-[#4b5563] transition hover:text-[#1040CC] hover:underline">
                {item.title}
              </Link>
            ))}
          </p>
          <p className="mt-3 text-xs text-[#9ca3af]">
            Canonical path: <span>{escapeHtml(documentDefinition.canonicalPath)}</span>
          </p>
        </footer>
      </div>

      <style>{`
        .legal-markdown h1 {
          font-size: 2rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          margin: 0 0 1rem;
          color: #111827;
        }
        .legal-markdown h2 {
          font-size: 1.4rem;
          font-weight: 600;
          letter-spacing: -0.02em;
          margin: 2rem 0 0.9rem;
          padding-top: 1rem;
          border-top: 1px solid rgba(17, 24, 39, 0.08);
          color: #111827;
        }
        .legal-markdown h3 {
          font-size: 1.05rem;
          font-weight: 600;
          margin: 1.4rem 0 0.6rem;
          color: #1f2937;
        }
        .legal-markdown h4 {
          font-size: 0.98rem;
          font-weight: 600;
          margin: 1.2rem 0 0.45rem;
          color: #374151;
        }
        .legal-markdown p,
        .legal-markdown li {
          font-size: 0.98rem;
          line-height: 1.8;
          color: #4b5563;
        }
        .legal-markdown p + p {
          margin-top: 0.9rem;
        }
        .legal-markdown ul,
        .legal-markdown ol {
          margin: 0.75rem 0 0.75rem 1.4rem;
        }
        .legal-markdown code {
          background: #f3f4f6;
          border-radius: 6px;
          padding: 0.1rem 0.35rem;
          font-family: "SF Mono", "Fira Code", monospace;
          font-size: 0.88em;
        }
        .legal-markdown a {
          color: #1040CC;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .legal-markdown hr {
          border: none;
          border-top: 1px solid rgba(17, 24, 39, 0.08);
          margin: 2rem 0;
        }
        .legal-markdown .table-wrap {
          overflow-x: auto;
          margin: 1rem 0;
        }
        .legal-markdown table {
          width: 100%;
          min-width: 28rem;
          border-collapse: collapse;
          font-size: 0.92rem;
        }
        .legal-markdown th,
        .legal-markdown td {
          border-bottom: 1px solid rgba(17, 24, 39, 0.08);
          padding: 0.7rem 0.75rem;
          text-align: left;
          vertical-align: top;
        }
        .legal-markdown th {
          background: #f9fafb;
          font-weight: 600;
          color: #1f2937;
        }
        @media (max-width: 768px) {
          .legal-markdown h1 {
            font-size: 1.6rem;
          }
          .legal-markdown h2 {
            font-size: 1.15rem;
          }
          .legal-markdown p,
          .legal-markdown li {
            font-size: 0.94rem;
          }
        }
      `}</style>
    </div>
  );
}

export default LegalDocumentPage;
